import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import {
	getFilterQuery,
	getFilterState,
	initialFilterState,
} from '@/stores/slices/filterSlice';
import { getTitle } from '@/stores/slices/titleSlice';
import { getNotDisabledCheckbox } from '@/stores/slices/modelsState';
import {
	getLanguagesWords,
	getNationalistWords,
} from '@/stores/slices/userSlice';

export const useFilterComputed = () => {
	const titleSelector = useSelector(getTitle);
	const { languages, nationalities } = useSelector(getNotDisabledCheckbox);
	const filterState = useSelector(getFilterState);
	const translateLanguages = useSelector(getLanguagesWords);
	const translateNationalist = useSelector(getNationalistWords);
	const filterQuery = useSelector(getFilterQuery);

	const filterStateIndicator = useMemo(
		() => JSON.stringify(filterState),
		[filterState],
	);

	const isFilterEmpty = useMemo(() => {
		return !filterQuery.length;
	}, [filterQuery]);

	const isAgeSelected = useMemo(() => {
		const res = filterQuery.some((item) => item.startsWith('age='));
		return res;
	}, [filterQuery]);

	const isPriceSelected = useMemo(() => {
		return filterQuery.some((item) => item.includes('price='));
	}, [filterQuery]);

	const isGenderOnly = useMemo(() => {
		if (filterQuery.length === 1 && filterQuery[0].includes('gender='))
			return true;
		return false;
	}, [filterStateIndicator]);

	const isOtherFilterActive = useMemo(() => {
		// Исключаемые категории
		const excludedCategories = ['status', 'gender', 'service_type'];

		// Проверяем остальные массивы фильтров (вторичные фильтры)
		const hasSecondaryFilters = Object.keys(initialFilterState)
			.filter(
				(key) =>
					!excludedCategories.includes(key) &&
					Array.isArray(filterState[key]),
			)
			.some((key) => filterState[key]?.length > 0);

		// Проверяем изменения в ranges.current_state (кроме дефолтных значений)
		const hasRangeChanges = Object.keys(
			filterState.ranges.default_state,
		).some((rangeKey) => {
			return filterQuery.some((i) => i.startsWith(`${rangeKey}=`));
			// const current = filterState.ranges?.current_state?.[rangeKey];
			// const def = filterState.ranges.default_state[rangeKey];
			// return JSON.stringify(current) !== JSON.stringify(def);
		});

		// Проверяем radius (если value не пустой)
		// const hasRadiusFilter = filterState.radius?.value?.length > 0;

		// console.log(
		// 	`=> hasSecondaryFilters: ${hasSecondaryFilters}, hasRangeChanges: ${hasRangeChanges}`,
		// );

		return hasSecondaryFilters || hasRangeChanges;
	}, [filterStateIndicator]);

	const isGenderAndServiceTypeOnly = useMemo(() => {
		// Исключаемые категории
		const excludedCategories = ['gender', 'service_type'];

		// Проверяем остальные массивы фильтров (вторичные фильтры)
		const hasSecondaryFilters = Object.keys(initialFilterState)
			.filter(
				(key) =>
					!excludedCategories.includes(key) &&
					Array.isArray(filterState[key]),
			)
			.some((key) => filterState[key]?.length > 0);

		// Проверяем изменения в ranges.current_state (кроме дефолтных значений)
		const hasRangeChanges = Object.keys(
			filterState.ranges.default_state,
		).some((rangeKey) => {
			return filterQuery.some((i) => i.startsWith(`${rangeKey}=`));
			// const current = filterState.ranges?.current_state?.[rangeKey];
			// const def = filterState.ranges.default_state[rangeKey];
			// return JSON.stringify(current) !== JSON.stringify(def);
		});

		// Проверяем radius (если value не пустой)
		// const hasRadiusFilter = filterState.radius?.value?.length > 0;

		// console.log(
		// 	`=> hasSecondaryFilters: ${hasSecondaryFilters}, hasRangeChanges: ${hasRangeChanges}`,
		// );

		return hasSecondaryFilters || hasRangeChanges;
	}, [filterStateIndicator]);

	const url_converted_tags = useMemo(() => {
		const mappings = {
			videochat: 'vchat',
			erocontent: 'ero',
			onlineorder: 'booking',
			verification: 'verified',
			elite: 'elitelady',
			trans: 'trans',
			couple: 'couple',
		};
		return titleSelector.map((word) => mappings[word] || word).join('-');
	}, [titleSelector]);

	const nationData = useMemo(() => {
		if (Array.isArray(translateNationalist)) {
			return translateNationalist?.filter((item2) =>
				nationalities?.some((item1) => item1 === item2.code),
			);
		} else {
			return [];
		}
	}, [nationalities, translateNationalist]);

	const langData = useMemo(() => {
		if (Array.isArray(translateNationalist)) {
			return translateLanguages?.filter((item2) =>
				languages?.some((item1) => item1 === item2.short_name),
			);
		} else return [];
	}, [languages, translateLanguages]);

	return {
		filterStateIndicator,
		isFilterEmpty,
		url_converted_tags,
		nationData,
		langData,
		isOtherFilterActive,
		isAgeSelected,
		isPriceSelected,
		isGenderOnly,
		isGenderAndServiceTypeOnly,
	};
};
